import { useMemo, useState } from "react";
import {
    MapPin,
    Mail,
    Building2,
    Search,
    SlidersHorizontal,
    CheckCircle2,
    Clock,
    PauseCircle,
} from "lucide-react";

const centres = [
    {
        name: "APIC Vizag Fab Lab",
        org: "Andhra Pradesh Innovation Corp",
        email: "vizag.centre@apic.gov.in",
        location: "Vizag",
        regNo: "APIC/CTR/2023/001",
        status: "Active",
    },
    {
        name: "APIC Vijayawada Prototyping Hub",
        org: "Andhra Pradesh Innovation Corp",
        email: "vijayawada.centre@apic.gov.in",
        location: "Vijayawada",
        regNo: "APIC/CTR/2023/002",
        status: "Active",
    },
    {
        name: "APIC Tirupati Design Studio",
        org: "Andhra Pradesh Innovation Corp",
        email: "tirupati.centre@apic.gov.in",
        location: "Tirupati",
        regNo: "APIC/CTR/2023/003",
        status: "Active",
    },
    {
        name: "APIC Kurnool Machine Shop",
        org: "Andhra Pradesh Innovation Corp",
        email: "kurnool.centre@apic.gov.in",
        location: "Kurnool",
        regNo: "APIC/CTR/2024/004",
        status: "Active",
    },
    {
        name: "APIC Guntur Electronics Bay",
        org: "Andhra Pradesh Innovation Corp",
        email: "guntur.centre@apic.gov.in",
        location: "Guntur",
        regNo: "APIC/CTR/2024/005",
        status: "Onboarding",
    },
    {
        name: "APIC Nellore Composites Lab",
        org: "Andhra Pradesh Innovation Corp",
        email: "nellore.centre@apic.gov.in",
        location: "Nellore",
        regNo: "APIC/CTR/2024/006",
        status: "Inactive",
    },
];

const statusStyle = {
    Active: "bg-green-100 text-green-600",
    Onboarding: "bg-orange-100 text-orange-500",
    Inactive: "bg-slate-100 text-slate-500",
};

const statusOptions = ["All Status", "Active", "Onboarding", "Inactive"];

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-slate-900">{value}</p>
                <p className="text-sm text-slate-500">{label}</p>
            </div>
        </div>
    );
}

export default function Centres() {
    const [query, setQuery] = useState("");
    const [status, setStatus] = useState("All Status");
    const [location, setLocation] = useState("All Locations");

    const locationOptions = useMemo(
        () => ["All Locations", ...new Set(centres.map((c) => c.location))],
        []
    );

    const counts = useMemo(() => {
        const active = centres.filter((c) => c.status === "Active").length;
        const onboarding = centres.filter((c) => c.status === "Onboarding").length;
        const inactive = centres.filter((c) => c.status === "Inactive").length;
        return { total: centres.length, active, onboarding, inactive };
    }, []);

    const filtered = useMemo(() => {
        return centres.filter((c) => {
            const matchesQuery =
                query.trim() === "" ||
                c.name.toLowerCase().includes(query.toLowerCase()) ||
                c.org.toLowerCase().includes(query.toLowerCase()) ||
                c.email.toLowerCase().includes(query.toLowerCase());

            const matchesStatus =
                status === "All Status" || c.status === status;

            const matchesLocation =
                location === "All Locations" || c.location === location;

            return matchesQuery && matchesStatus && matchesLocation;
        });
    }, [query, status, location]);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-slate-900">Centres</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        All fabrication and prototyping centres registered across Andhra Pradesh
                    </p>
                </div>

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={Building2} label="Total centres" value={counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard icon={CheckCircle2} label="Active" value={counts.active} tone="bg-green-50 text-green-500" />
                    <StatCard icon={Clock} label="Onboarding" value={counts.onboarding} tone="bg-orange-50 text-orange-500" />
                    <StatCard icon={PauseCircle} label="Inactive" value={counts.inactive} tone="bg-slate-100 text-slate-500" />
                </div>

                {/* Table card */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-6 border-b border-slate-100">
                        <div>
                            <h2 className="text-xl font-bold text-slate-900">
                                Registered Centres
                            </h2>
                            <p className="text-slate-500 text-sm mt-0.5">
                                {filtered.length} of {centres.length} centres across
                                Andhra Pradesh
                            </p>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <div className="relative">
                                <Search
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search name, org, email..."
                                    className="pl-9 pr-3 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition w-full sm:w-64"
                                />
                            </div>

                            <select
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                className="text-sm rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition"
                            >
                                {locationOptions.map((loc) => (
                                    <option key={loc} value={loc}>
                                        {loc}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                className="text-sm rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 transition"
                            >
                                {statusOptions.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>

                            {(query || status !== "All Status" || location !== "All Locations") && (
                                <button
                                    onClick={() => {
                                        setQuery("");
                                        setStatus("All Status");
                                        setLocation("All Locations");
                                    }}
                                    className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 px-3 py-2 transition"
                                >
                                    <SlidersHorizontal size={14} />
                                    Reset
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
                        <table className="w-full min-w-[860px] text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 bg-slate-50">
                                    <th className="px-6 py-3 font-semibold">
                                        Centre Name
                                    </th>
                                    <th className="px-6 py-3 font-semibold">
                                        Organization
                                    </th>
                                    <th className="px-6 py-3 font-semibold">
                                        Email
                                    </th>
                                    <th className="px-6 py-3 font-semibold">
                                        Location
                                    </th>
                                    <th className="px-6 py-3 font-semibold">
                                        Registration No.
                                    </th>
                                    <th className="px-6 py-3 font-semibold">
                                        Status
                                    </th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-slate-100">
                                {filtered.map((centre, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-slate-50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
                                                    <Building2 size={16} />
                                                </div>
                                                <span className="font-semibold text-slate-900">
                                                    {centre.name}
                                                </span>
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 text-slate-600">
                                            {centre.org}
                                        </td>

                                        <td className="px-6 py-4 text-slate-600">
                                            <div className="flex items-center gap-1.5">
                                                <Mail
                                                    size={14}
                                                    className="text-slate-400"
                                                />
                                                {centre.email}
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 text-slate-600">
                                            <div className="flex items-center gap-1.5">
                                                <MapPin
                                                    size={14}
                                                    className="text-slate-400"
                                                />
                                                {centre.location}
                                            </div>
                                        </td>

                                        <td className="px-6 py-4 text-slate-500">
                                            {centre.regNo}
                                        </td>

                                        <td className="px-6 py-4">
                                            <span
                                                className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyle[centre.status]}`}
                                            >
                                                {centre.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}

                                {filtered.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={6}
                                            className="px-6 py-12 text-center text-slate-400"
                                        >
                                            No centres match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}